import { useMemo } from 'react';

import { useCurrentUser } from '@/entities/user';

/**
 * Reads the server-owned "last connected" timestamp from the signed-in user —
 * the previous login session, surfaced by platform-api on `GET /auth/me`. This
 * is the "last visited" baseline for the Opportunities Room "new since last
 * visit" KPI.
 *
 * Returns `null` on the first-ever login (no prior session), in which case
 * callers should treat every opportunity as new. A malformed (non-ISO) value —
 * only reachable via a platform-api contract violation, since the field is
 * server-owned — is also collapsed to `null`: degrading to "everything is new"
 * is intentional and preferable to letting an Invalid Date reach
 * `lastVisitedAt.toISOString()` downstream, which would throw during render.
 *
 * Memoised on the raw ISO string so the returned `Date` is referentially stable
 * across renders (it feeds query keys and `useMemo` dependencies downstream).
 */
export function useLastConnectedAt(): Date | null {
  const user = useCurrentUser();
  const raw = user?.lastConnectedAt ?? null;
  return useMemo(() => {
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [raw]);
}
